/**
 * <rm-carousel> — scroll-snap carousel with prev/next buttons and an optional progress bar.
 *
 * Markup contract:
 *   <rm-carousel>
 *     <ul data-track> <li data-slide>…</li> … </ul>
 *     <button data-prev> <button data-next>
 *     <span data-progress></span>   (optional; its width is set to the scroll progress)
 *   </rm-carousel>
 * The element gets [data-static] when everything fits and no scrolling is needed.
 */
if (!customElements.get('rm-carousel')) {
  class RmCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.progress = this.querySelector('[data-progress]');
      this.rail = this.querySelector('[data-rail]');
      this.dynamicButton = this.querySelector('[data-dynamic-button]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.update = this.update.bind(this);
      this.onPrev = () => this.scrollPage(-1);
      this.onNext = () => this.scrollPage(1);

      this.track.addEventListener('scroll', this.update, { passive: true });
      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.update);
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.resizeObserver?.disconnect();
    }

    scrollPage(direction) {
      const slide = this.track.querySelector('[data-slide]');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({
        left: direction * step * perPage,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const position = this.track.scrollLeft;
      this.toggleAttribute('data-static', max <= 1);
      this.setDisabled(this.prevButton, position <= 1);
      this.setDisabled(this.nextButton, position >= max - 1);
      this.updateDynamicButton();
      if (this.rail && this.track.scrollWidth > 0) {
        this.rail.style.setProperty('--rm-thumb-size', `${(this.track.clientWidth / this.track.scrollWidth) * 100}%`);
        this.rail.style.setProperty('--rm-thumb-offset', `${(position / this.track.scrollWidth) * 100}%`);
      }
      if (this.progress) {
        const ratio = this.track.scrollWidth > 0 ? (position + this.track.clientWidth) / this.track.scrollWidth : 1;
        this.progress.style.setProperty('--rm-progress', `${Math.round(ratio * 100)}%`);
      }
    }

    // Button label/link follow the card that fills most of the visible track ("Shop [name]")
    updateDynamicButton() {
      const button = this.dynamicButton;
      if (!button) return;
      const template = button.dataset.labelTemplate || '';
      if (!template.includes('[name]')) return;
      const view = this.track.getBoundingClientRect();
      let best = null;
      let bestVisible = -1;
      this.track.querySelectorAll('[data-slide]').forEach((slide) => {
        const box = slide.getBoundingClientRect();
        const visible = Math.min(box.right, view.right) - Math.max(box.left, view.left);
        if (visible > bestVisible + 1) {
          best = slide;
          bestVisible = visible;
        }
      });
      if (!best || !best.dataset.name) return;
      const label = template.replace('[name]', best.dataset.name);
      if (button.textContent !== label) button.textContent = label;
      button.setAttribute('href', best.dataset.url || button.dataset.defaultUrl || '#');
    }

    setDisabled(button, disabled) {
      if (!button) return;
      button.setAttribute('aria-disabled', String(disabled));
      button.toggleAttribute('disabled', disabled);
    }
  }

  customElements.define('rm-carousel', RmCarousel);
}
